<template>
  <div class="sci-page-wrapper" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-[1280px] mx-auto px-4 space-y-8">
      
      <!-- Welcome Header -->
      <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 class="sci-page-title-only">Painel de Controle (Prefeitura)</h1>
          <p class="text-slate-500 font-medium mt-1">Visão global da sua prefeitura referente a {{ currentMonthName }} de {{ currentYearName }}</p>
        </div>
        <div class="w-full md:w-80">
          <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Filtrar por Fornecedor</label>
          <v-autocomplete
            v-model="selectedProvider"
            :items="providers"
            item-text="provider_name"
            item-value="provider_id"
            placeholder="Todos os Fornecedores"
            solo flat dense hide-details
            clearable
            class="bg-white border border-slate-200 rounded-lg custom-select-main font-bold"
            height="48"
          />
        </div>
      </div>

      <!-- Alertas de CNH -->
      <div v-if="cnhAlerts.length > 0" class="mb-8 animate-fade-in-up">
        <div class="bg-gradient-to-r from-red-500 to-rose-600 rounded-2xl p-4 shadow-xl shadow-red-500/20 relative overflow-hidden">
          <div class="absolute -right-4 -top-4 opacity-[0.15] transform rotate-12 pointer-events-none">
            <v-icon size="100" color="white">mdi-alert-decagram</v-icon>
          </div>
          <div class="relative z-10">
            <div class="flex items-center justify-between cursor-pointer" @click="showCnhAlerts = !showCnhAlerts">
              <div class="flex items-center gap-3">
                <div class="bg-white/20 p-2 rounded-xl backdrop-blur-sm border border-white/20">
                  <v-icon color="white" small>mdi-card-account-details-alert</v-icon>
                </div>
                <div>
                  <h3 class="text-white font-black text-sm uppercase tracking-widest">
                    Atenção Necessária
                  </h3>
                  <p class="text-white/80 text-[11px] font-bold mt-0.5">
                    {{ cnhAlerts.length }} motorista(s) com CNH vencida ou a vencer em 30 dias
                  </p>
                </div>
              </div>
              <div class="bg-white/10 hover:bg-white/20 p-1.5 rounded-lg transition-colors border border-white/10">
                <v-icon color="white" small>{{ showCnhAlerts ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </div>
            </div>
            
            <div v-show="showCnhAlerts" class="mt-4 pt-4 border-t border-white/20">
              <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                <div v-for="driver in cnhAlerts" :key="driver.driver_id" 
                     class="bg-white rounded-xl p-3 flex items-center gap-3 shadow-sm hover:shadow-md transition-shadow cursor-default group">
                  <div :class="['p-2 rounded-lg flex-shrink-0 transition-colors', isCnhExpired(driver.driver_date_validity) ? 'bg-red-100 group-hover:bg-red-200' : 'bg-orange-100 group-hover:bg-orange-200']">
                    <v-icon :color="isCnhExpired(driver.driver_date_validity) ? 'red-darken-2' : 'orange-darken-2'" small>
                      {{ isCnhExpired(driver.driver_date_validity) ? 'mdi-alert-circle' : 'mdi-clock-alert' }}
                    </v-icon>
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-[11px] font-black text-slate-700 uppercase truncate" :title="driver.driver_name">{{ driver.driver_name }}</p>
                    <div class="flex items-center gap-1.5 mt-0.5">
                      <span :class="['text-[10px] font-bold px-1.5 py-0.5 rounded text-white', isCnhExpired(driver.driver_date_validity) ? 'bg-red-500' : 'bg-orange-500']">
                        {{ isCnhExpired(driver.driver_date_validity) ? 'VENCIDA' : 'VENCE EM ' + daysToCnhExpire(driver.driver_date_validity) + ' DIAS' }}
                      </span>
                      <span class="text-[10px] font-bold text-slate-500">
                        {{ formatDateCnh(driver.driver_date_validity) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Prime KPIs (Finance and Volume) -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Investimento Total -->
        <div class="bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl p-8 shadow-2xl shadow-slate-900/10 relative overflow-hidden transition-transform duration-300 hover:-translate-y-1 hover:shadow-slate-900/20">
          <div class="absolute -right-10 -top-10 opacity-5">
            <v-icon size="180" color="white">mdi-currency-brl</v-icon>
          </div>
          <div class="relative z-10 flex flex-col h-full justify-between min-h-[160px]">
            <div class="flex items-center gap-3 mb-6">
              <div class="bg-white/10 p-2.5 rounded-xl border border-white/5">
                <v-icon color="white" small>mdi-cash-multiple</v-icon>
              </div>
              <h3 class="text-white/80 font-bold uppercase tracking-widest text-xs">INVESTIMENTO TOTAL (MÊS)</h3>
            </div>
            <div>
              <p class="text-4xl lg:text-5xl font-black text-white tracking-tighter">
                R$ <span class="font-mono">{{ data.currentMonth ? data.currentMonth.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00' }}</span>
              </p>
              <p class="text-emerald-400 font-bold text-xs mt-3 flex items-center gap-1 bg-emerald-400/10 w-fit px-3 py-1 rounded-full border border-emerald-400/20">
                <v-icon small color="emerald" class="mr-1">mdi-trending-up</v-icon> Somatório deste mês considerando notas e faturas
              </p>
            </div>
          </div>
        </div>

        <!-- Volume Total -->
        <div class="bg-gradient-to-br from-orange-500 to-[#FF4600] rounded-3xl p-8 shadow-2xl shadow-orange-500/20 relative overflow-hidden transition-transform duration-300 hover:-translate-y-1 hover:shadow-orange-500/30">
          <div class="absolute -right-6 -bottom-6 opacity-[0.08] transform rotate-12">
            <v-icon size="160" color="white">mdi-gas-station</v-icon>
          </div>
          <div class="relative z-10 flex flex-col h-full justify-between min-h-[160px]">
            <div class="flex items-center gap-3 mb-6">
              <div class="bg-white/20 p-2.5 rounded-xl border border-white/10">
                <v-icon color="white" small>mdi-water-outline</v-icon>
              </div>
              <h3 class="text-white/90 font-bold uppercase tracking-widest text-xs tracking-widest">VOLUME ABASTECIDO (MÊS)</h3>
            </div>
            <div>
              <p class="text-4xl lg:text-5xl font-black text-white tracking-tighter">
                <span class="font-mono">{{ data.litersPerMonth ? data.litersPerMonth.toLocaleString('pt-BR', { minimumFractionDigits: 1 }) : '0,0' }}</span> <span class="text-3xl lg:text-4xl text-white/80 font-bold ml-1">L</span>
              </p>
              <p class="text-white font-bold text-xs mt-3 bg-white/20 w-fit px-3 py-1 rounded-full border border-white/10">
                Consumo total da frota local
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Comparison Lists -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- Licitado -->
        <div class="bg-gradient-to-br from-slate-50 to-white rounded-3xl p-6 border-t-4 border-t-slate-400 border border-slate-200 shadow-sm flex flex-col relative overflow-hidden transition-all hover:shadow-md min-h-[220px]">
           <div class="absolute -right-4 -bottom-4 opacity-[0.03] transform rotate-12">
             <v-icon size="120" color="black">mdi-file-document-check-outline</v-icon>
           </div>
           <div class="relative z-10 flex items-center justify-between mb-4">
             <h3 class="text-[11px] font-black italic text-slate-700 uppercase tracking-widest">LICITADO LT</h3>
             <div class="bg-slate-100 p-2 rounded-lg"><v-icon color="slate-500" small>mdi-file-document-check-outline</v-icon></div>
           </div>
           <div class="relative z-10 flex-grow py-2">
             <List :data="dataDashboard.licitado" color="transparent" />
           </div>
        </div>

        <!-- Abastecido -->
        <div class="bg-gradient-to-br from-orange-50/50 to-white rounded-3xl p-6 border-t-4 border-t-orange-500 border border-slate-200 shadow-sm flex flex-col relative overflow-hidden transition-all hover:shadow-md min-h-[220px]">
           <div class="absolute -right-4 -bottom-4 opacity-[0.03] transform rotate-12">
             <v-icon size="120" color="black">mdi-gas-station-outline</v-icon>
           </div>
           <div class="relative z-10 flex items-center justify-between mb-4">
             <h3 class="text-[11px] font-black italic text-orange-900 uppercase tracking-widest">ABASTECIDO LT</h3>
             <div class="bg-orange-100 p-2 rounded-lg"><v-icon color="orange" small>mdi-gas-station-outline</v-icon></div>
           </div>
           <div class="relative z-10 flex-grow py-2">
             <List :data="dataDashboard.saidas" color="transparent" />
           </div>
        </div>

        <!-- Disponivel -->
        <div class="bg-gradient-to-br from-emerald-50/50 to-white rounded-3xl p-6 border-t-4 border-t-emerald-500 border border-slate-200 shadow-sm flex flex-col relative overflow-hidden transition-all hover:shadow-md min-h-[220px]">
           <div class="absolute -right-4 -bottom-4 opacity-[0.03] transform rotate-12">
             <v-icon size="120" color="black">mdi-check-circle-outline</v-icon>
           </div>
           <div class="relative z-10 flex items-center justify-between mb-4">
             <h3 class="text-[11px] font-black italic text-emerald-900 uppercase tracking-widest">DISPONÍVEL LT</h3>
             <div class="bg-emerald-100 p-2 rounded-lg"><v-icon color="success" small>mdi-check-circle-outline</v-icon></div>
           </div>
           <div class="relative z-10 flex-grow py-2">
             <List :data="dataDashboard.totais" color="transparent" />
           </div>
        </div>
      </div>

      <!-- Charts Row -->
      <div class="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <!-- Comparativo Chart -->
        <div class="bg-white rounded-3xl border border-slate-200 shadow-sm p-8">
           <div class="flex items-center justify-between mb-8">
             <h3 class="text-sm font-black text-slate-800 uppercase tracking-widest">COMPARATIVO DE DESPESAS</h3>
             <span class="bg-slate-100 text-slate-500 text-[10px] px-3 py-1.5 rounded-lg font-bold uppercase tracking-widest">MÊS ANTERIOR VS ATUAL</span>
           </div>
           <div style="height: 350px;" class="w-full">
            <TechBarChart 
              :chartData="[data.lastMonth, data.currentMonth]" 
              :labels="['Mês Anterior', 'Mês Atual']"
            />
           </div>
        </div>

        <!-- Anual Chart -->
        <div class="bg-white rounded-3xl border border-slate-200 shadow-sm p-8">
           <div class="flex items-center justify-between mb-8">
             <h3 class="text-sm font-black text-slate-800 uppercase tracking-widest">DESPESAS NO ANO</h3>
             <span class="bg-orange-100 text-[#FF4600] text-[10px] px-3 py-1.5 rounded-lg font-bold uppercase tracking-widest">CONTRATADO VS UTILIZADO</span>
           </div>
           <div style="height: 350px;" class="w-full">
            <TechLineChart 
              :chartData="[data.licitacao, data.currentYear]" 
              :labels="['Total Licitado', 'Gasto no Ano']"
              lineColor="#27ae60"
            />
           </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import Helpers from "@/mixins/helpers";
import List from "./components/ListCompare";
import TechBarChart from "./components/TechBarChart";
import TechLineChart from "./components/TechLineChart";

export default {
  components: { 
    List,
    TechBarChart,
    TechLineChart
  },
  props: ["prefecture_id"],
  mixins: [Helpers],
  data: () => ({
    isCustomDashboard: false,
    dataDashboard: {
      licitado: [],
      saidas: [],
      totais: [],
    },
    data: {
      licitacao: 0,
      saidas: 0,
      total: 0,
      currentMonth: 0,
      lastMonth: 0,
      currentYear: 0,
      litersPerMonth: 0,
    },
    cnhAlerts: [],
    showCnhAlerts: false,
    providers: [],
    selectedProvider: null,
  }),
  computed: {
    currentMonthName() {
      const months = [
        "JANEIRO", "FEVEREIRO", "MARÇO", "ABRIL", "MAIO", "JUNHO",
        "JULHO", "AGOSTO", "SETEMBRO", "OUTUBRO", "NOVEMBRO", "DEZEMBRO"
      ];
      return months[new Date().getMonth()];
    },
    currentYearName() {
      return new Date().getFullYear();
    }
  },
  watch: {
    selectedProvider() {
      this.initialize();
      this.loadDashboardTotals();
    }
  },
  created() {
    this.Helpers;
    this.fetchProviders();
    this.initialize();
  },
  mounted() {
    this.loadDashboardTotals();
  },
  methods: {
    fetchProviders() {
      this.$http(`/providers/prefecture/${this.prefecture_id}`).then((r) => {
        this.providers = r.data;
      });
    },
    initialize() {
      const q = this.selectedProvider ? `?provider_id=${this.selectedProvider}` : '';
      this.$http(`/dashboard/prefectures/${this.prefecture_id}${q}`).then(
        (r) => (this.data = r.data)
      );
      this.fetchDrivers();
    },
    loadDashboardTotals() {
      const q = this.selectedProvider ? `?provider_id=${this.selectedProvider}` : '';
      this.$http(`/dashboard/prefecturesII/${this.prefecture_id}${q}`).then(
        (r) => (this.dataDashboard = r.data)
      );
    },
    fetchDrivers() {
      this.$http(`/drivers/prefectures/${this.prefecture_id}`).then((r) => {
        this.processDriversCnh(r.data);
      });
    },
    processDriversCnh(drivers) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      this.cnhAlerts = drivers.filter(driver => {
        if (!driver.driver_date_validity || !driver.driver_active) return false;
        
        let validityDate;
        // Check if date is in DD/MM/YYYY format or ISO format
        if (driver.driver_date_validity.includes('/')) {
          const [day, month, year] = driver.driver_date_validity.split('/');
          validityDate = new Date(year, month - 1, day);
        } else {
          validityDate = new Date(driver.driver_date_validity);
          // Adjust timezone offset to compare correctly
          validityDate = new Date(validityDate.getTime() + Math.abs(validityDate.getTimezoneOffset() * 60000));
        }
        
        validityDate.setHours(0, 0, 0, 0);
        
        const diffTime = validityDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        return diffDays <= 30; // Expired (<= 0) or expiring in 30 days
      });
 
      // Sort: Expired first, then by nearest expiration
      this.cnhAlerts.sort((a, b) => {
        const dateA = this.parseDateCnh(a.driver_date_validity);
        const dateB = this.parseDateCnh(b.driver_date_validity);
        return dateA - dateB;
      });
    },
    parseDateCnh(dateStr) {
      if (!dateStr) return new Date();
      if (dateStr.includes('/')) {
        const [day, month, year] = dateStr.split('/');
        return new Date(year, month - 1, day);
      }
      const d = new Date(dateStr);
      return new Date(d.getTime() + Math.abs(d.getTimezoneOffset() * 60000));
    },
    isCnhExpired(dateStr) {
      const validityDate = this.parseDateCnh(dateStr);
      validityDate.setHours(0, 0, 0, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return validityDate.getTime() < today.getTime();
    },
    daysToCnhExpire(dateStr) {
      const validityDate = this.parseDateCnh(dateStr);
      validityDate.setHours(0, 0, 0, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const diffTime = validityDate.getTime() - today.getTime();
      return Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
    },
    formatDateCnh(dateStr) {
      if (!dateStr) return '';
      if (dateStr.includes('/')) return dateStr;
      const d = this.parseDateCnh(dateStr);
      return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
    }
  },
};
</script>
 
<style scoped>
.custom-select-main >>> .v-input__control .v-input__slot {
  background: transparent !important;
  box-shadow: none !important;
  padding: 0 16px !important;
}

.custom-select-main >>> input,
.custom-select-main >>> .v-select__selection {
  text-transform: uppercase;
  font-weight: 600;
  font-size: 0.8rem;
  color: #334155 !important;
}
</style>